import React, { useCallback } from "react";
import Icons from "@hs/icons";
import { useSelector } from "react-redux";
import CountBadge from "components/CountBadge";
import Divider from "components/Divider";
import { getLocalizedText, getClassNames } from "data/utils";
import {
    SearchItemType,
    type SAYTOptionDTO,
    type SearchScopeEnum,
} from "data/types";
import SearchParam, { getLocalizedSearchLabel } from "components/SearchParam";
import maskStyles from "./Mask.module.css";
import styles from "./Recents.module.css";
import { globalSearchOptionsSelector } from "data/reducers/selectors";

const searchTypeIcon = {
    [SearchItemType.Favorite]: "fa-regular fa-star",
    [SearchItemType.Recent]: "clock-rotate-left",
    [SearchItemType.Freetext]: "magnifying-glass",
};

type Props = {
    sayt: string,
    itemUri: string,
    searchScope: SearchScopeEnum,
    onSearch: (item: SAYTOptionDTO, itemUri: ?string) => void,
    defaultExpanded: boolean,
};

const Recents = ({
    sayt,
    itemUri,
    searchScope,
    onSearch,
    defaultExpanded,
}: Props) => {
    const handleClick = useCallback(
        (item: SAYTOptionDTO) => (e) => {
            e.preventDefault();
            onSearch(item, itemUri, searchScope);
        },
        [itemUri, searchScope, onSearch]
    );
    const saytOptions: Array<SAYTOptionDTO> = useSelector(
        globalSearchOptionsSelector
    );
    const isLoading = saytOptions == null;
    const isEmpty = !isLoading && saytOptions.length === 0;
    const hasRecents = !isLoading && saytOptions.length > 0;

    return (
        <Divider
            name="showSearchRecents"
            hideHR
            className={maskStyles.header}
            defaultExpanded={defaultExpanded}
            header={
                <>
                    {getLocalizedText("$search:mask.recent")}
                    <Icons.Library
                        name="rectangle-vertical-history"
                        fixedWidth
                        className={maskStyles.icon}
                    />
                </>
            }
        >
            <div className="recents">
                {isLoading && (
                    <div
                        className={getClassNames(
                            styles.recent,
                            styles.nointeraction
                        )}
                    >
                        {getLocalizedText("$search.loading")}
                    </div>
                )}
                {isEmpty && (
                    <div
                        className={getClassNames(
                            styles.recent,
                            styles.nointeraction
                        )}
                    >
                        {getLocalizedText("$search.prompt")}
                    </div>
                )}
                {hasRecents &&
                    saytOptions.map((option, i) => {
                        const optionTypeName: string =
                            searchTypeIcon[
                                option.item?.searchType ??
                                    SearchItemType.Freetext
                            ];
                        const searchLabel = getLocalizedSearchLabel(
                            option.label,
                            option.item?.param
                        );
                        return (
                            <div
                                key={`searchmask.recent.${i}`}
                                className={getClassNames(
                                    styles.recent,
                                    sayt === option.label && styles.current
                                )}
                                onClick={handleClick(option)}
                            >
                                <Icons.Library
                                    name={optionTypeName}
                                    className={styles.icon}
                                />
                                {option.label === "N/A" ? (
                                    <i className={styles.empty}>
                                        {searchLabel}
                                    </i>
                                ) : (
                                    <span className={styles.text}>
                                        {searchLabel}
                                    </span>
                                )}
                                {option.item?.param?.length > 0 && (
                                    <div className={styles.params}>
                                        {option.item?.param?.map(
                                            (param, id) => (
                                                <SearchParam.Label
                                                    key={`searchmask.recent.${i}.label.${id}`}
                                                    className={styles.param}
                                                    param={param}
                                                />
                                            )
                                        )}
                                    </div>
                                )}
                                <CountBadge
                                    className={styles.badge}
                                    count={option.count}
                                />
                            </div>
                        );
                    })}
            </div>
        </Divider>
    );
};
Recents.displayName = "SearchMask.Recents";
export default Recents;
